import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useAdminAuth } from '@/context/AdminAuthContext';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  LogOut, Calendar, Package, Plus, FileText,
  Clock, TrendingUp, ShoppingBag, RefreshCw, Box, Bell, BellOff, Volume2,
  Smartphone, AlertCircle, IndianRupee, Receipt, Gift
} from 'lucide-react';
import AdminOrdersList from './components/AdminOrdersList';
import AdminCalendar from './components/AdminCalendar';
import AdminTodayOrders from './components/AdminTodayOrders';
import AdminCreateOrder from './components/AdminCreateOrder';
import AdminCustomInvoice from './components/AdminCustomInvoice';
import AdminSubscriptions from './components/AdminSubscriptions';
import AdminProducts from './components/AdminProducts';
import AdminOutstanding from './components/AdminOutstanding';
import AdminExpenses from './components/AdminExpenses';
import AdminHamperPricing from './components/AdminHamperPricing';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const NOTIFICATION_SOUND = 'data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2teleogAAACb2Oumm3E8J1uo2O2sdUgOJoe62du8jEoAAACX2OummnBELVuo2O2sdUgOJoe62du8jEoAAACX2OummnBELVuo2O2sdUgOJoe62du8jEoAAACX2OummnBELVuo2O2rdUkOJYi62du8jEoAAACX2OummnBELVuo2O2sdUgOJoe62du8i0sAAACX2OummnBELVuo2O2sdUgOJoe62du8jEoAAACV2OummnFELVqp2O2sdUkPJYi62du8jEoAAACX2OummnBELVuo2O2sdUgOJoe62du8jEoAAACX2OummnBELVuo2O2sdUgOJoe62du8jEoAAACX2OummnBELVuo2O2sdUgOJoe62du8jEoAAACX2OummnBELVuo2O2sdUgOJoe62du8jEoAAACX2OummnBELVuo2O2sdUgOJoe62du8jEoAAACX2OummnBELVuo2O2sdUgOJoe62du8jEo=';

/** Convert a base64url string to Uint8Array for VAPID */
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  return new Uint8Array([...rawData].map(c => c.charCodeAt(0)));
}

const AdminDashboard = () => {
  const { admin, adminToken, logout, loading } = useAdminAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [activeTab, setActiveTab] = useState('orders');
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);
  const [notificationPermission, setNotificationPermission] = useState('default');
  const [pushSupported, setPushSupported] = useState(false);
  const [lastOrderCount, setLastOrderCount] = useState(null);
  const [deferredPrompt, setDeferredPrompt] = useState(null);     // PWA install
  const [showInstallBanner, setShowInstallBanner] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);
  const audioRef = useRef(null);
  const pollIntervalRef = useRef(null);

  // Detect standalone (already installed) and PWA install prompt
  useEffect(() => {
    const standalone = window.matchMedia('(display-mode: standalone)').matches
      || window.navigator.standalone === true;
    setIsStandalone(standalone);

    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      if (!standalone) setShowInstallBanner(true);
    };
    window.addEventListener('beforeinstallprompt', handler);

    // On iOS, show manual install banner if not standalone
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    if (isIOS && !standalone && window.location.pathname.startsWith('/admin')) {
      setShowInstallBanner(true);
    }

    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  // Check push support & permission
  useEffect(() => {
    const supported = 'serviceWorker' in navigator && 'PushManager' in window;
    setPushSupported(supported);
    if ('Notification' in window) {
      setNotificationPermission(Notification.permission);
      if (Notification.permission === 'granted') setNotificationsEnabled(true);
    }
  }, []);

  // Subscribe to VAPID push notifications
  const subscribeToPush = useCallback(async () => {
    try {
      const sw = await navigator.serviceWorker.ready;

      // Check if already subscribed
      const existing = await sw.pushManager.getSubscription();
      if (existing) {
        // Re-send to backend in case it was lost
        await axios.post(`${API}/admin/push/subscribe`, existing.toJSON(), {
          headers: { Authorization: `Bearer ${adminToken}` }
        });
        return true;
      }

      // Get VAPID public key from backend
      const keyRes = await axios.get(`${API}/admin/push/vapid-key`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      const vapidKey = keyRes.data.public_key;
      const applicationServerKey = urlBase64ToUint8Array(vapidKey);

      const subscription = await sw.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey
      });

      // Send subscription to backend
      await axios.post(`${API}/admin/push/subscribe`, subscription.toJSON(), {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      return true;
    } catch (err) {
      console.error('Push subscribe failed:', err);
      return false;
    }
  }, [adminToken]);

  const requestNotificationPermission = async () => {
    if (!('Notification' in window)) {
      toast.error('Your browser does not support notifications');
      return;
    }
    try {
      const permission = await Notification.requestPermission();
      setNotificationPermission(permission);
      if (permission === 'granted') {
        setNotificationsEnabled(true);
        playNotificationSound();

        // Try VAPID push subscription (unsubscribe old first to force fresh key)
        if (pushSupported) {
          try {
            const sw = await navigator.serviceWorker.ready;
            const old = await sw.pushManager.getSubscription();
            if (old) await old.unsubscribe();   // Clear stale subscription
          } catch {}
          const ok = await subscribeToPush();
          if (ok) {
            toast.success('Push notifications enabled! You\'ll get alerts even when the app is closed.');
          } else {
            toast.success('In-app notifications enabled!');
          }
        } else {
          toast.success('Notifications enabled!');
        }
      } else {
        toast.error('Notification permission denied');
      }
    } catch (error) {
      console.error('Failed to request notification permission:', error);
      toast.error('Failed to enable notifications');
    }
  };

  const playNotificationSound = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.currentTime = 0;
      audioRef.current.play().catch(() => {});
    }
  }, []);

  const showNotification = useCallback((title, body, orderId) => {
    if (notificationsEnabled && Notification.permission === 'granted') {
      playNotificationSound();
      const n = new Notification(title, {
        body,
        icon: '/icons/icon-192.png',
        tag: orderId || 'new-order',
        requireInteraction: true,
        vibrate: [200, 100, 200]
      });
      n.onclick = () => { window.focus(); setActiveTab('orders'); n.close(); };
      setTimeout(() => n.close(), 10000);
    }
  }, [notificationsEnabled, playNotificationSound]);

  // Polling fallback (also works when push isn't available)
  const checkForNewOrders = useCallback(async () => {
    if (!adminToken || !notificationsEnabled) return;
    try {
      const res = await axios.get(`${API}/admin/stats`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      const count = res.data.total_orders;
      if (lastOrderCount !== null && count > lastOrderCount) {
        const n = count - lastOrderCount;
        showNotification('🛒 New Order!', `${n} new order${n > 1 ? 's' : ''}. Tap to view.`, `order-${Date.now()}`);
        setStats(res.data);
        toast.success('New order received!', { description: 'Check the Orders tab', duration: 5000 });
      }
      setLastOrderCount(count);
    } catch {}
  }, [adminToken, notificationsEnabled, lastOrderCount, showNotification]);

  useEffect(() => {
    if (adminToken && notificationsEnabled) {
      pollIntervalRef.current = setInterval(checkForNewOrders, 15000);
      checkForNewOrders();
    }
    return () => clearInterval(pollIntervalRef.current);
  }, [adminToken, notificationsEnabled, checkForNewOrders]);

  useEffect(() => {
    if (!loading && !admin) navigate('/admin/login');
  }, [admin, loading, navigate]);

  useEffect(() => {
    if (adminToken) fetchStats();
  }, [adminToken]);

  const fetchStats = async () => {
    try {
      const res = await axios.get(`${API}/admin/stats`, { headers: { Authorization: `Bearer ${adminToken}` } });
      setStats(res.data);
      if (lastOrderCount === null) setLastOrderCount(res.data.total_orders);
    } catch {}
  };

  const handleLogout = () => {
    logout();
    toast.success('Logged out successfully');
    navigate('/admin/login');
  };

  const handleInstallPWA = async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') toast.success('App installed!');
      setDeferredPrompt(null);
      setShowInstallBanner(false);
    }
    // iOS: just show instruction
  };

  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
      </div>
    );
  }

  if (!admin) return null;

  return (
    <div className="min-h-screen bg-gray-50">
      <audio ref={audioRef} preload="auto">
        <source src={NOTIFICATION_SOUND} type="audio/wav" />
      </audio>

      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-3 sm:px-6">
          <div className="flex items-center justify-between h-14">
            <div className="flex items-center gap-2.5">
              <div className="w-9 h-9 bg-primary rounded-lg flex items-center justify-center">
                <Package className="h-5 w-5 text-white" />
              </div>
              <div>
                <h1 className="text-base font-bold text-accent leading-none">Admin</h1>
                <p className="text-[10px] text-muted-foreground">HHouse of Breadss</p>
              </div>
              {isStandalone && (
                <Badge variant="outline" className="text-[10px] text-green-600 border-green-300 hidden sm:flex">
                  <Smartphone className="h-2.5 w-2.5 mr-1" />App
                </Badge>
              )}
            </div>
            <div className="flex items-center gap-1.5">
              <Button
                variant={notificationsEnabled ? 'default' : 'outline'}
                size="sm"
                onClick={() => notificationsEnabled ? (setNotificationsEnabled(false), toast.info('Notifications off')) : requestNotificationPermission()}
                className={`h-8 px-2.5 gap-1.5 ${notificationsEnabled ? 'bg-green-600 hover:bg-green-700' : ''}`}
                title={notificationsEnabled ? 'Notifications on' : 'Enable notifications'}
              >
                {notificationsEnabled ? <Bell className="h-3.5 w-3.5" /> : <BellOff className="h-3.5 w-3.5" />}
                <span className="hidden sm:inline text-xs">{notificationsEnabled ? 'On' : 'Notify'}</span>
              </Button>
              <Button variant="outline" size="sm" onClick={handleLogout} className="h-8 px-2.5 gap-1.5">
                <LogOut className="h-3.5 w-3.5" />
                <span className="hidden sm:inline text-xs">Logout</span>
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* PWA Install Banner */}
      {showInstallBanner && !isStandalone && (
        <div className="bg-amber-600 text-white px-4 py-2.5">
          <div className="max-w-7xl mx-auto flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 text-sm">
              <Smartphone className="h-4 w-4 shrink-0" />
              {isIOS
                ? <span>Install app: tap <strong>Share</strong> then <strong>"Add to Home Screen"</strong> for push notifications</span>
                : <span>Install as app for push notifications & faster access</span>
              }
            </div>
            <div className="flex gap-2 shrink-0">
              {!isIOS && deferredPrompt && (
                <Button size="sm" variant="secondary" className="h-7 text-xs" onClick={handleInstallPWA}>Install</Button>
              )}
              <button onClick={() => setShowInstallBanner(false)} className="text-white/80 hover:text-white text-lg leading-none">×</button>
            </div>
          </div>
        </div>
      )}

      {/* Notification permission banner */}
      {!notificationsEnabled && notificationPermission === 'default' && !showInstallBanner && (
        <div className="bg-amber-50 border-b border-amber-200 px-4 py-2">
          <div className="max-w-7xl mx-auto flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <Bell className="h-4 w-4 text-amber-600 shrink-0" />
              <p className="text-xs text-amber-800">Enable notifications to get alerted when new orders come in</p>
            </div>
            <Button size="sm" onClick={requestNotificationPermission} className="bg-amber-600 hover:bg-amber-700 h-7 text-xs gap-1 shrink-0">
              <Bell className="h-3 w-3" />Enable
            </Button>
          </div>
        </div>
      )}

      <main className="max-w-7xl mx-auto px-3 sm:px-6 py-4">
        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            {[
              { label: "Today's Orders", value: stats.today_orders, icon: ShoppingBag, color: 'blue' },
              { label: 'Pending', value: stats.pending_orders, icon: Clock, color: 'orange' },
              { label: "Today's Revenue", value: `₹${stats.today_revenue}`, icon: TrendingUp, color: 'green' },
              { label: 'Total Orders', value: stats.total_orders, icon: Package, color: 'purple' },
            ].map(({ label, value, icon: Icon, color }) => (
              <Card key={label} className="p-3">
                <div className="flex items-center gap-2.5">
                  <div className={`w-9 h-9 bg-${color}-100 rounded-lg flex items-center justify-center shrink-0`}>
                    <Icon className={`h-4 w-4 text-${color}-600`} />
                  </div>
                  <div>
                    <p className="text-xs text-muted-foreground leading-none mb-0.5">{label}</p>
                    <p className="text-xl font-bold text-accent leading-none">{value}</p>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}

        {/* Tabs — 8 tabs, scrollable on mobile */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
          <div className="overflow-x-auto -mx-3 px-3 sm:mx-0 sm:px-0 pb-1">
            <TabsList className="inline-flex min-w-full sm:grid sm:grid-cols-10 gap-0.5 h-9">
              {[
                { value: 'orders',      icon: Package,     label: 'Orders'   },
                { value: 'products',    icon: Box,         label: 'Products' },
                { value: 'subscriptions', icon: RefreshCw, label: 'Subs'     },
                { value: 'calendar',    icon: Calendar,    label: 'Calendar' },
                { value: 'today',       icon: Clock,       label: 'Today'    },
                { value: 'create',      icon: Plus,        label: 'Create'   },
                { value: 'custom',      icon: FileText,    label: 'Invoice'  },
                { value: 'outstanding', icon: IndianRupee, label: 'Dues'     },
                { value: 'expenses',    icon: Receipt,     label: 'Expenses' },
                { value: 'hampers',     icon: Gift,        label: 'Hampers'  },
              ].map(({ value, icon: Icon, label }) => (
                <TabsTrigger key={value} value={value} className="gap-1 px-2 text-xs whitespace-nowrap">
                  <Icon className="h-3.5 w-3.5 shrink-0" />
                  <span>{label}</span>
                </TabsTrigger>
              ))}
            </TabsList>
          </div>

          <TabsContent value="orders"><AdminOrdersList adminToken={adminToken} /></TabsContent>
          <TabsContent value="products"><AdminProducts adminToken={adminToken} /></TabsContent>
          <TabsContent value="subscriptions"><AdminSubscriptions adminToken={adminToken} /></TabsContent>
          <TabsContent value="calendar"><AdminCalendar adminToken={adminToken} /></TabsContent>
          <TabsContent value="today"><AdminTodayOrders adminToken={adminToken} /></TabsContent>
          <TabsContent value="create"><AdminCreateOrder adminToken={adminToken} onSuccess={fetchStats} /></TabsContent>
          <TabsContent value="custom"><AdminCustomInvoice adminToken={adminToken} onSuccess={fetchStats} /></TabsContent>
          <TabsContent value="outstanding"><AdminOutstanding adminToken={adminToken} /></TabsContent>
          <TabsContent value="expenses"><AdminExpenses adminToken={adminToken} /></TabsContent>
          <TabsContent value="hampers"><AdminHamperPricing adminToken={adminToken} /></TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default AdminDashboard;
